// Design-system demo for BC-18 message shortcuts + AI suggestions.
// Lives on /design so visual changes can be reviewed in isolation before
// hooking into the live Conversation page.

import { useRef, useState } from 'react'
import { Send, Sparkles, Smile } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { SuggestionBar, type Suggestion, type AiDraftSuggestion } from '@/components/inbox/SuggestionBar'
import { EmojiShortcodePopover } from '@/components/inbox/EmojiShortcodePopover'
import { useEmojiShortcode } from '@/hooks/useEmojiShortcode'
import { CannedAnswersList } from '@/components/shortcuts/CannedAnswersList'
import { ClustersList } from '@/components/shortcuts/ClustersList'
import { AiSuggestionsToggle } from '@/components/shortcuts/AiSuggestionsToggle'

// Sample data lifted from the prod data we mined in OWR.
const SAMPLE_SUGGESTIONS: Suggestion[] = [
  { id: 'a1', title: 'Merge duplicate profiles', body: 'Ah, I see — I can merge the 2 {{profile_url}} — Sound good?', tags: ['merge', 'profiles'], variables: ['profile_url'] },
  { id: 'a2', title: 'Investigating', body: "Cheers I'll take a look", tags: ['support'], variables: [] },
  { id: 'a3', title: 'Request screenshot', body: 'Can you send me a screenshot?', tags: ['support'], variables: [] },
  { id: 'a4', title: 'Confirm fix shipped', body: 'That should be fixed now mate', tags: ['support'], variables: [] }
]

const SAMPLE_AI_DRAFT: AiDraftSuggestion = {
  id: 'd1',
  body: "Already merged that one — let me know if it's all sorted now",
  confidence: 0.92,
  based_on_canned_id: 'a1'
}

const SAMPLE_CANNED = SAMPLE_SUGGESTIONS.map((s, i) => ({
  id: s.id,
  title: s.title,
  body: s.body,
  tags: s.tags,
  status: 'active',
  use_count: [42, 38, 21, 18][i] ?? 1,
  variables: s.variables,
  updated_at: new Date().toISOString()
}))

const SAMPLE_CLUSTERS = [
  { id: 'c1', suggested_title: 'Merge duplicate profiles', suggested_tags: ['merge'], canonical_phrasing: 'Ah, I see — I can merge the 2 {{profile_url}} — Sound good?', size: 42, promoted_at: null, dismissed_at: null },
  { id: 'c2', suggested_title: 'Investigating the issue', suggested_tags: ['support'], canonical_phrasing: "Cheers I'll take a look", size: 38, promoted_at: null, dismissed_at: null },
  { id: 'c3', suggested_title: 'Request evidence', suggested_tags: ['request-info'], canonical_phrasing: 'Can you send me {{evidence}}?', size: 21, promoted_at: null, dismissed_at: null }
]

export function ShortcutsDemo() {
  return (
    <div className="space-y-12">
      <section>
        <h3 className="text-lg font-semibold mb-3">Composer with suggestions</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Live preview of the agent composer with the SuggestionBar above the textarea and the <code>:</code> emoji
          autocomplete popover. Number keys <kbd>⌘1</kbd>–<kbd>⌘9</kbd> insert a chip.
        </p>
        <ComposerPreview />
      </section>

      <section>
        <h3 className="text-lg font-semibold mb-3">Admin · suggested answers</h3>
        <p className="text-sm text-muted-foreground mb-4">
          The /shortcuts admin page. Library on left, AI-discovered clusters middle, AI settings right.
        </p>

        <div className="space-y-4">
          <CannedAnswersList answers={SAMPLE_CANNED} />
          <ClustersList clusters={SAMPLE_CLUSTERS} />
          <AiSuggestionsToggle enabled={true} />
        </div>
      </section>

      <section>
        <h3 className="text-lg font-semibold mb-3">Emoji shortcode reference</h3>
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Common shortcodes</CardTitle>
            <CardDescription>Type <code>:</code> in the composer to open. Adds ✅/❌ to the default picker too.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
              {[
                [':check:', '✅'], [':cross:', '❌'], [':tada:', '🎉'], [':fire:', '🔥'],
                [':pray:', '🙏'], [':eyes:', '👀'], [':bug:', '🐛'], [':rocket:', '🚀']
              ].map(([code, emoji]) => (
                <div key={code} className="flex items-center gap-2 bg-muted/30 rounded-md px-3 py-2">
                  <span className="text-xl">{emoji}</span>
                  <span className="font-mono text-xs text-muted-foreground">{code}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </section>
    </div>
  )
}

function ComposerPreview() {
  const [value, setValue] = useState("Hey Will — :")
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const shortcode = useEmojiShortcode({ value, onChange: setValue, textareaRef })

  const handlePick = (s: Suggestion) => {
    setValue(prev => prev ? prev + (prev.endsWith(' ') ? '' : ' ') + s.body : s.body)
  }

  return (
    <div className="border border-border bg-card rounded-lg">
      <div className="px-4 py-3 border-b border-border">
        <div className="text-sm font-medium">Will Hudson</div>
        <div className="text-xs text-muted-foreground">@willh · 4 tournaments</div>
      </div>
      <div className="p-4 space-y-3 min-h-[120px]">
        <div className="inline-block max-w-[70%] bg-muted rounded-lg rounded-bl-sm px-3 py-2 text-sm">
          Hey mate — I think I have a duplicate profile on OWR?
        </div>
        <br />
        <div className="inline-block max-w-[70%] bg-muted rounded-lg rounded-bl-sm px-3 py-2 text-sm">
          Here's the second one: https://oldworldrankings.com/@willh-dk
        </div>
      </div>

      <div className="border-t border-border bg-card p-4 space-y-2">
        <SuggestionBar
          suggestions={SAMPLE_SUGGESTIONS}
          allSuggestions={SAMPLE_SUGGESTIONS}
          aiDraft={SAMPLE_AI_DRAFT}
          showShortcuts={true}
          onPickSuggestion={handlePick}
          onPickDraft={(d) => setValue(d.body)}
        />

        <form className="flex gap-2 items-end relative" onSubmit={(e) => e.preventDefault()}>
          {shortcode.active && (
            <EmojiShortcodePopover
              matches={shortcode.matches}
              selectedIndex={shortcode.selectedIndex}
              onSelect={shortcode.insert}
              onHover={shortcode.select}
            />
          )}
          <Button type="button" variant="ghost" size="icon" className="cursor-pointer shrink-0" title="Emoji">
            <Smile className="size-4" />
          </Button>
          <textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => { shortcode.handleKeyDown(e) }}
            placeholder="Type a reply… use : for emoji"
            className="flex-1 bg-muted rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none min-h-[40px] max-h-[120px]"
            rows={2}
          />
          <Button type="submit" disabled className="cursor-pointer shrink-0">
            <Send className="size-4" />
          </Button>
        </form>
        <div className="text-[11px] text-muted-foreground/70 px-1 flex justify-between">
          <span>⏎ send · ⇧⏎ newline · <span className="font-mono">:</span> emoji · <span className="font-mono">⌘1-9</span> chip</span>
          <span className="flex items-center gap-1"><Sparkles className="size-3" /> gemini-flash-lite-latest</span>
        </div>
      </div>
    </div>
  )
}

export default ShortcutsDemo
